import React, { useState, useEffect } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Clock,
  FileText,
  GraduationCap,
  Info,
  Lightbulb,
  Lock,
  Target,
  Sparkles,
  Calendar,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════
   PRE-ASSESSMENT EXPLAINER
   Shows purpose, time commitment, and deliverables
   before any assessment begins
   ═══════════════════════════════════════════════ */

const PILLAR_ICONS = {
  leadership_governance: "Leadership & Governance",
  business_finance: "Business & Finance",
  student_support: "Student Support",
  academic_compliance: "Academic & Compliance",
  data_ai_readiness: "Data & AI Readiness",
};

export function PreAssessmentExplainer({ offering, onStart, onBack }) {
  const [showPillars, setShowPillars] = useState(false);

  if (!offering) return null;

  const isStaged = offering.staged;
  const pillars = offering.pillars || [];

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="pre-assessment-explainer">
      {/* Header */}
      <div className="text-center space-y-3">
        <Badge className="rounded-full border-[#bfdbfe] bg-[#eff6ff] px-4 py-1.5 text-sm text-[#1a56db]">
          {offering.price === "Free" ? "Free" : offering.price}
        </Badge>
        <h2 className="text-2xl font-bold text-foreground" data-testid="explainer-title">
          {offering.title}
        </h2>
        <p className="text-base text-[#4b5563] max-w-xl mx-auto leading-relaxed">
          {offering.description}
        </p>
      </div>

      {/* Key info cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="rounded-xl border-[#e5e7eb] bg-[#fafafa] p-4 text-center">
          <Clock className="h-5 w-5 mx-auto mb-2 text-[#1a56db]" />
          <div className="text-xs text-[#6b7280] mb-1">Time Commitment</div>
          <div className="text-sm font-semibold text-foreground">{offering.time_commitment}</div>
        </Card>
        <Card className="rounded-xl border-[#e5e7eb] bg-[#fafafa] p-4 text-center">
          <Target className="h-5 w-5 mx-auto mb-2 text-[#1a56db]" />
          <div className="text-xs text-[#6b7280] mb-1">Purpose</div>
          <div className="text-sm font-semibold text-foreground">
            {offering.key === "initial" ? "Identify top opportunities" :
             offering.key === "caporilli_diagnostic" ? "School quality evaluation" :
             "Deep diagnostic + action plan"}
          </div>
        </Card>
        <Card className="rounded-xl border-[#e5e7eb] bg-[#fafafa] p-4 text-center">
          <FileText className="h-5 w-5 mx-auto mb-2 text-[#1a56db]" />
          <div className="text-xs text-[#6b7280] mb-1">Deliverables</div>
          <div className="text-sm font-semibold text-foreground">
            {offering.deliverables?.length || 0} outputs
          </div>
        </Card>
      </div>

      {/* How results are used */}
      <Card className="rounded-xl border-[#e5e7eb] bg-[#fafafa] p-5">
        <h3 className="text-sm font-semibold text-[#111827] mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#1a56db]" />
          What You'll Receive
        </h3>
        <ul className="space-y-2">
          {(offering.deliverables || []).map((d, i) => (
            <li key={i} className="flex gap-2 text-sm text-[#4b5563]">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#059669]" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Staged assessment pillars (for Caporilli + Detailed) */}
      {isStaged && pillars.length > 0 && (
        <Card className="rounded-xl border-[#e5e7eb] bg-[#fafafa] p-5">
          <button
            type="button"
            onClick={() => setShowPillars(!showPillars)}
            className="w-full flex items-center justify-between text-sm font-semibold text-foreground"
          >
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#1a56db]" />
              {offering.total_days}-Day Structure
            </span>
            {showPillars ? <ChevronUp className="h-4 w-4 text-[#9ca3af]" /> : <ChevronDown className="h-4 w-4 text-[#9ca3af]" />}
          </button>

          {showPillars && (
            <div className="mt-4 space-y-3">
              {pillars.map((p) => (
                <div key={p.key} className="flex items-start gap-3 py-2 px-3 rounded-lg bg-[#fafafa] border border-[#e5e7eb]">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#eff6ff] text-[#1a56db] text-xs font-bold">
                    D{p.day}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">{p.label}</div>
                    <div className="text-xs text-[#6b7280] mt-0.5">
                      ~{p.time_estimate_minutes} min &middot; {p.question_count || "~10"} questions
                    </div>
                    {p.description && (
                      <p className="text-xs text-[#9ca3af] mt-1">{p.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* CTA */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-center pt-2">
        <Button
          onClick={onStart}
          className="h-12 rounded-xl bg-[#1a56db] px-8 text-white text-sm font-semibold hover:bg-[#1a56db]/90 transition-transform hover:scale-[1.02]"
          data-testid="start-assessment-btn"
        >
          {offering.key === "initial" ? "Start Free Assessment" :
           offering.key === "caporilli_diagnostic" ? "Begin MySchoolRx" :
           "Begin Assessment"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        {onBack && (
          <Button
            variant="outline"
            onClick={onBack}
            className="h-12 rounded-xl border-[#e5e7eb] text-foreground"
            data-testid="back-to-offerings-btn"
          >
            Back to Options
          </Button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ASSESSMENT OFFERINGS PAGE
   Shows available assessments for an industry
   ═══════════════════════════════════════════════ */

export function AssessmentOfferings({ industry, onSelectOffering }) {
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/assessments/offerings/${industry || "education"}`);
        const data = await res.json();
        setOfferings(data.offerings || []);
      } catch {
        setOfferings([]);
      }
      setLoading(false);
    };
    load();
  }, [industry]);

  if (loading) {
    return (
      <div className="text-center py-12 text-[#9ca3af]">
        Loading assessment options...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assessment-offerings">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-bold text-foreground">
          Assessment Options
        </h2>
        <p className="text-sm text-[#4b5563] max-w-lg mx-auto">
          Choose the assessment that's right for your organization. Start free or go deeper with a comprehensive diagnostic.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto">
        {offerings.map((o) => (
          <Card
            key={o.key}
            className={[
              "rounded-xl border-[#e5e7eb] bg-[#fafafa] p-5 cursor-pointer transition-all hover:border-[#1a56db]/40 hover:shadow-md",
              o.key === "initial" ? "ring-1 ring-[#1a56db]/30" : "",
            ].join(" ")}
            onClick={() => onSelectOffering(o)}
            data-testid={`offering-${o.key}`}
          >
            <div className="flex items-center justify-between mb-3">
              <Badge className={`text-[10px] rounded-full ${
                o.price === "Free"
                  ? "bg-[#d1fae5] text-[#10b981] border-emerald-500/20"
                  : "bg-[#eff6ff] text-[#1a56db] border-[#bfdbfe]"
              }`}>
                {o.price}
              </Badge>
              {o.staged && (
                <span className="text-[10px] text-[#9ca3af]">
                  {o.total_days}-day staged
                </span>
              )}
            </div>

            <h3 className="text-base font-semibold text-foreground">{o.title}</h3>
            <p className="text-sm text-[#6b7280] mt-1 leading-relaxed line-clamp-2">
              {o.description}
            </p>

            <div className="mt-3 flex items-center gap-2 text-xs text-[#9ca3af]">
              <Clock className="h-3 w-3" />
              <span>{o.time_commitment}</span>
            </div>

            <div className="mt-3 pt-3 border-t border-[#e5e7eb]">
              <div className="text-xs text-[#1a56db] font-medium flex items-center gap-1">
                Learn more <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   STAGED ASSESSMENT PROGRESS TRACKER
   Shows day-by-day progress for multi-day assessments
   ═══════════════════════════════════════════════ */

export function StagedProgressTracker({ pillars, currentDay, completedDays }) {
  if (!pillars || pillars.length === 0) return null;

  const completed = new Set(completedDays || []);

  return (
    <div className="rounded-xl border border-[#e5e7eb] bg-[#fafafa] p-4" data-testid="staged-progress-tracker">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-4 w-4 text-[#1a56db]" />
        <span className="text-sm font-semibold text-foreground">Assessment Progress</span>
        <span className="text-xs text-[#9ca3af] ml-auto">
          {completed.size}/{pillars.length} days complete
        </span>
      </div>

      <div className="flex items-center gap-1">
        {pillars.map((p, i) => {
          const isDone = completed.has(p.day);
          const isCurrent = p.day === currentDay;
          const isLocked = p.day > currentDay && !isDone;

          return (
            <React.Fragment key={p.key}>
              {i > 0 && (
                <div className={`flex-1 h-0.5 ${isDone || isCurrent ? "bg-[#1a56db]" : "bg-[#f9fafb]"}`} />
              )}
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all ${
                  isDone
                    ? "bg-[#10b981] text-white"
                    : isCurrent
                    ? "bg-[#1a56db] text-white ring-2 ring-[#1a56db]/30"
                    : "bg-[#f9fafb] text-[#9ca3af]"
                }`}
                title={p.label}
              >
                {isDone ? <Check className="h-3.5 w-3.5" /> : isLocked ? <Lock className="h-3 w-3" /> : p.day}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="mt-3 text-center">
        <div className="text-xs text-[#6b7280]">
          {pillars.find(p => p.day === currentDay)?.label || ""}
        </div>
      </div>
    </div>
  );
}
